import { useCallback, useEffect, useRef, useMemo } from 'react';

/**
 * A React hook to handle setTimeout and clearTimeout easily.
 *
 * CRITICAL: Both the individual functions AND the return object MUST be memoized to prevent infinite loops.
 *
 * This hook supports two usage patterns:
 * 1. Object usage: `const timeout = useTimeout(); useEffect(() => {}, [timeout])`
 * 2. Destructured usage: `const { set, clear } = useTimeout(); useEffect(() => {}, [set, clear])`
 *
 * Without proper memoization, both patterns would cause infinite loops:
 * - Pattern 1: New object reference every render → effect runs infinitely
 * - Pattern 2: New function references every render → effect runs infinitely
 *
 * Example of the problem (WITHOUT memoization):
 * ```tsx
 * // Pattern 1 - object in dependency array
 * const timeout = useTimeout(); // NEW object every render
 * useEffect(() => {
 *   timeout.set(() => console.log('hi'), 1000);
 * }, [timeout]); // ❌ Infinite loop!
 *
 * // Pattern 2 - destructured functions in dependency array
 * const { set, clear } = useTimeout(); // NEW functions every render
 * useEffect(() => {
 *   set(() => console.log('hi'), 1000);
 * }, [set, clear]); // ❌ Infinite loop!
 * ```
 *
 * Solution: useCallback for stable functions + useMemo for stable object wrapper.
 */
export function useTimeout() {
  const timeoutId = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
      }
    };
  }, []);

  // Stable function references (never change across renders)
  const set = useCallback((callback: () => void, delay: number) => {
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
    }
    timeoutId.current = setTimeout(callback, delay);
  }, []);

  const clear = useCallback(() => {
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
      timeoutId.current = null;
    }
  }, []);

  // Stable object reference (never changes across renders)
  // Supports both: `const timeout = useTimeout()` and `const { set } = useTimeout()`
  return useMemo(() => ({ set, clear }), [set, clear]);
}
